/**
 * Dubizzle-style bottom navigation bar — visible only on mobile (≤768px).
 *
 * Five tabs: Home · Browse · Post (+) · Chat · Account
 * The center "Post" button is a raised FAB.
 *
 * IMPORTANT: This component is purely additive — it does NOT modify any
 * existing component. It renders on top of everything via `position: fixed`.
 */
import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getUser } from "../../redux/slices/AuthUser";
import { useUnreadCount } from "../chat/hooks/useUnreadCount";
import {
  Box,
  Badge,
  Typography,
} from "@mui/material";

// Icons
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ChatBubbleRoundedIcon from "@mui/icons-material/ChatBubbleRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";

const NAV_HEIGHT = 64;

const tabs = [
  { key: "home",    label: "Home",    icon: HomeRoundedIcon,       path: "/" },
  { key: "browse",  label: "Browse",  icon: SearchRoundedIcon,     path: "/browse-listing" },
  { key: "post",    label: "Post",    icon: AddRoundedIcon,        path: "/add-new-product" },
  { key: "chat",    label: "Chat",    icon: ChatBubbleRoundedIcon, path: "/chat" },
  { key: "account", label: "Account", icon: PersonRoundedIcon,     path: "/profile" },
];

function getActiveTab(pathname) {
  if (pathname === "/") return "home";
  if (pathname.startsWith("/browse-listing") || pathname.startsWith("/categories") || pathname.startsWith("/popular-products")) return "browse";
  if (pathname.startsWith("/add-new-product") || pathname.startsWith("/editProduct")) return "post";
  if (pathname.startsWith("/chat")) return "chat";
  if (
    pathname.startsWith("/profile") ||
    pathname.startsWith("/settings") ||
    pathname.startsWith("/account-info") ||
    pathname.startsWith("/my-products") ||
    pathname.startsWith("/favorite-products") ||
    pathname.startsWith("/wallet") ||
    pathname.startsWith("/payment-settings") ||
    pathname.startsWith("/transactions")
  )
    return "account";
  return null;
}

export default function MobileBottomNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector(getUser);
  const unreadMessages = useUnreadCount();
  const activeTab = getActiveTab(pathname);

  // Hide on admin pages and auth pages
  const hideOnPaths = ["/admins", "/login", "/signup", "/reset-password", "/verification"];
  if (hideOnPaths.some((p) => pathname.startsWith(p))) return null;

  const handleNav = useCallback(
    (tab) => {
      // Auth-protected tabs
      if (!user && (tab.key === "chat" || tab.key === "account" || tab.key === "post")) {
        window.dispatchEvent(
          new CustomEvent("directrent:open-auth", { detail: { mode: "login" } })
        );
        return;
      }
      navigate(tab.path);
    },
    [navigate, user]
  );

  return (
    <Box
      component="nav"
      aria-label="Mobile navigation"
      sx={{
        display: { xs: "flex", md: "none" },       // mobile only
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        height: `${NAV_HEIGHT}px`,
        backgroundColor: "rgba(255,255,255,0.96)",
        backdropFilter: "saturate(180%) blur(20px)",
        borderTop: "1px solid rgba(0,0,0,0.08)",
        zIndex: 1300,
        justifyContent: "space-around",
        alignItems: "center",
        px: 0.5,
        // Safe-area padding for iPhone notch / gesture bar
        pb: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab.key;
        const isPost = tab.key === "post";
        const Icon = tab.icon;

        // Center FAB-style "Post" button
        if (isPost) {
          return (
            <Box
              key={tab.key}
              onClick={() => handleNav(tab)}
              role="button"
              tabIndex={0}
              aria-label="Post an item"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                mt: "-22px", // raise above the bar
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #FE1F02 0%, #FF6B4A 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 16px rgba(254,31,2,0.35)",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "scale(1.08)",
                    boxShadow: "0 6px 22px rgba(254,31,2,0.45)",
                  },
                  "&:active": {
                    transform: "scale(0.95)",
                  },
                }}
              >
                <AddRoundedIcon sx={{ color: "#fff", fontSize: 30 }} />
              </Box>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  mt: 0.3,
                  color: isActive ? "primary.main" : "text.secondary",
                }}
              >
                {tab.label}
              </Typography>
            </Box>
          );
        }

        // Regular tab
        return (
          <Box
            key={tab.key}
            onClick={() => handleNav(tab)}
            role="button"
            tabIndex={0}
            aria-label={tab.label}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              py: 0.6,
              px: 1.2,
              borderRadius: "12px",
              transition: "all 0.2s ease",
              minWidth: 56,
              "&:active": {
                transform: "scale(0.92)",
              },
            }}
          >
            <Badge
              badgeContent={tab.key === "chat" ? unreadMessages : 0}
              color="error"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: 9,
                  minWidth: 16,
                  height: 16,
                  top: 2,
                  right: -2,
                },
              }}
            >
              <Icon
                sx={{
                  fontSize: 26,
                  color: isActive ? "primary.main" : "rgba(0,0,0,0.45)",
                  transition: "color 0.2s ease",
                }}
              />
            </Badge>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: isActive ? 700 : 500,
                mt: 0.2,
                color: isActive ? "primary.main" : "rgba(0,0,0,0.5)",
                transition: "color 0.2s ease",
                lineHeight: 1,
              }}
            >
              {tab.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

/** CSS class to add bottom padding so content isn't hidden behind the nav */
export const MOBILE_NAV_HEIGHT = NAV_HEIGHT;
